import initialState from 'reducers/initialState';
import { normalize } from 'normalizr';
import { merge } from 'lodash';
import { collection } from 'utils/immutable';
import { emptyObject } from 'constants/emptyConst';
import {
  LOAD_OFFER_ASSET_CLIENT_SUCCEEDED,
  SAVE_OFFER_ASSET_CLIENT_SUCCEEDED,
  OFFER_ASSET_CLIENT_DATA_INVALIDATED
} from 'features/offer/edit/assets/client/actionTypes';
import { OFFER_ASSET_CLIENT_UPDATED } from 'features/offer/edit/assets/client/eventTypes';
import {
  LOAD_OFFER_PARTNER_SUCCEEDED,
  LOAD_OFFER_ASSOCIATED_PARTNERS_SUCCEEDED,
  SAVE_OFFER_PARTNER_SUCCEEDED,
  LOAD_CLIENT_ADDRESSES_SUCCEEDED
} from 'features/offer/edit/client/form/actionTypes';
import { OFFER_PARTNER_UPDATED, OFFER_ASSOCIATED_PARTNER_DELETED } from 'features/offer/edit/client/form/eventTypes';
import {
  loadOfferPartnerSchema,
  offerPartnerAddressesSchema,
  loadOfferPartnerVerificationsSchema,
  loadOfferPartnerEligibilitiesSchema,
  loadOfferPartnerKnockoutCriteriaSchema,
  loadOfferPartnerConsentsSchema,
  loadOfferPartnerBankAccountsSchema,
  loadOfferPartnerMonthlyObligationsSchema,
  loadOfferPartnerActivitiesSchema,
  loadOfferPartnerIncomeTypesSchema,
  loadOfferPartnerIncomeDataSchema,
  loadOfferPartnerQuestionsSchema,
  loadOfferPartnerQuestionActionsSchema,
  partnerSchema,
  decisionSchema,
  exposureSchema,
  checkListSchema,
  documentsSchema,
  leasingExpensesSchema,
  commissionsSchema,
  positionsSchema
} from 'features/common/schemas';
import schemas from '../../../utils/schemas';
import { defaultPartnerId } from 'features/common/selectors';
import { LOAD_PARTNER_VERIFICATIONS_SUCCEEDED } from 'features/offer/edit/client/verification/actionTypes';
import { LOAD_PARTNER_MONTHLY_OBLIGATIONS_SUCCEEDED } from 'features/offer/edit/client/monthlyObligations/actionTypes';
import { SET_DEFAULT_ADDRESS_ON_OFFER_CLIENT } from 'features/offer/edit/client/form/actionTypes';
import { LOAD_ACTIVITY_FIELDS_SUCCEEDED, UPDATE_ACTIVITY_FIELDS_SUCCEEDED } from 'features/offer/edit/client/activities/actionTypes';
import { LOAD_PARTNER_CONSENTS_SUCCEEDED } from 'features/offer/edit/client/consent/actionTypes';
import { LOAD_PARTNER_BANK_ACCOUNTS_SUCCEEDED } from 'features/offer/edit/client/bankAccount/actionTypes';
import {
  LOAD_OFFER_PARTNER_INCOME_TYPES_SUCCEEDED,
  LOAD_OFFER_PARTNER_INCOME_DATA_SUCCEEDED
} from 'features/offer/edit/client/income/actionTypes';
import { OFFER_PARTNER_INCOME_TYPE_UPDATED } from 'features/offer/edit/client/income/eventTypes';
import { OFFER_PARTNER_CONSENTS_UPDATED } from 'features/offer/edit/client/consent/eventTypes';
import { OFFER_PARTNER_MONTHLY_OBLIGATIONS_UPDATED } from 'features/offer/edit/client/monthlyObligations/eventTypes';
import { LOAD_FOLDER_PARTNER_SUCCESS } from 'features/folder/wizard/actionTypes';
import {
  LOAD_FINANCED_OFFERS_SUCCEEDED,
  LOAD_CLIENT_OFFERS_SUCCEEDED,
  LOAD_FOLDER_OFFER_PARTNERS_SUCCEEDED,
  BLOCK_FACILITY_SUBLIMIT_AMOUNT_MANUALLY_STARTED,
  BLOCK_FACILITY_SUBLIMIT_AMOUNT_MANUALLY_SUCCEEDED,
  BLOCK_FACILITY_SUBLIMIT_AMOUNT_MANUALLY_FAILED
} from 'features/folder/edit/generalData/financedOffers/actionTypes';
import { LOAD_FINANCED_OFFERS_FOR_CONTRACT_GENERATION_SUCCEEDED } from 'features/document/edit/contractGeneration/actionTypes';
import { LOAD_FOLDER_OFFERS_SUCCEEDED } from 'features/document/edit/decision/warranties/actionTypes';
import { LOAD_DECISION_SUMMARY_SUCCESS, LOAD_CONTRACT_GEN_PERMISSIONS_SUCCESS } from 'features/document/edit/decision/summary/actionTypes';
import {
  LOAD_APPROVAL_LEVELS_SUCCESS,
  APPROVAL_LEVEL_UPDATED,
  LOAD_APPROVALS_HISTORY_SUCCESS
} from 'features/document/edit/decision/approvalLevels/actionTypes';
import { LOAD_DOCUMENT_SCORING_RESULT_SUCCESS, LOAD_DOCUMENT_SCORING_GRIDS_SUCCESS } from 'features/document/edit/scoring/actionTypes';
import {
  LOAD_DOCUMENT_SCORING_HISTORY_SESSION_SUCCESS,
  LOAD_DOCUMENT_SCORING_HISTORY_SESSION_GRIDS_SUCCESS
} from 'features/document/edit/scoringHistory/actionTypes';
import {
  DOCUMENT_SCORING_KNOCKOUT_CRITERIA_UPDATED,
  DOCUMENT_SCORING_CATEGORY_UPDATED,
  DOCUMENT_SCORING_FINANCIAL_INDICATORS_UPDATED,
  DOCUMENT_FINANCIAL_INDICATOR_DIMENSIONS_UPDATED,
  DOCUMENT_SCORING_INVALIDATED
} from 'features/document/edit/scoring/eventTypes';
import { OFFER_PARTNER_KNOCKOUT_CRITERIA_UPDATED } from 'features/offer/edit/client/knockoutCriteria/eventTypes';
import { FOLDER_FINANCED_OFFERS_UPDATED } from 'features/folder/edit/generalData/financedOffers/eventTypes';
import {
  LOAD_WARRANTIES_DATA_SUCCESS,
  LOAD_WARRANTIES_SUMMARY_SUCCESS,
  WARRANTY_DELETED,
  ALL_WARRANTIES_DELETED
} from 'features/document/edit/financials/warranty/actionTypes';
import {
  LOAD_DOCUMENT_PROPERTIES_SUCCESS,
  UPDATE_DOCUMENT_PROPERTIES_SUCCEEDED
} from 'features/document/edit/documentProperties/actionTypes';
import { WARRANTIES_INVALIDATED } from 'features/document/edit/financials/warranty/eventTypes';
import { OFFER_PARTNER_ALL_INCOME_TYPES_INVALIDATED } from 'features/offer/edit/client/income/eventTypes';
import { LOAD_EXPOSURE_SUCCESS } from 'features/document/edit/exposure/actionTypes';
import { EXPOSURE_CURRENCIES_RETRIEVED } from 'features/document/edit/exposure/eventTypes';
import { LOAD_STATE_TRANSITIONS_SUCCESS } from 'features/document/edit/stateTransitions/actionTypes';
import { LOAD_CHECKLIST_SUCCESS } from 'features/document/edit/checklist/actionTypes';
import { CHECKLIST_INVALIDATED } from 'features/document/edit/checklist/eventTypes';
import { LOAD_LEASING_EXPENSES_SUCCESS, LEASING_EXPENSES_UPDATED } from 'features/offer/edit/financials/leasingExpenses/actionTypes';
import { OFFER_PARTNER_DELETED, OFFER_PARTNER_RESET } from 'features/offer/edit/client/form/eventTypes';
import { LOAD_DOCUMENT_SIGNEES_SUCCEEDED, DOCUMENT_SIGNEES_UPDATED } from 'features/document/edit/documents/actionTypes';
import { DOCUMENT_LIST_EXPORTED, DOCUMENT_SIGNEES_INVALIDATED } from 'features/document/edit/documents/eventTypes';
import { LOAD_PROPERTY_DETAILS_SUCCESS } from 'features/offer/edit/assets/asset/actionTypes';
import {
  LOAD_COMMISSIONS_SUCCESS,
  LOAD_CALCULATION_METHODS_SUCCESS,
  COMMISSIONS_UPDATED,
  COMMISSION_COMPUTED,
  COMMISSION_ADDED,
  COMMISSION_DELETED
} from 'features/offer/edit/financials/commissions/actionTypes';
import {
  LOAD_POSITIONS_SUCCEEDED,
  LOAD_CONTACT_PERSONS_SUCCEEDED,
  UPDATE_CONTACT_PERSONS_SUCCEEDED
} from 'features/offer/edit/client/contactPersons/actionTypes';
import { LOAD_GUARANTORS_SUCCEEDED } from 'features/document/edit/documents/actionTypes';
import { LOAD_SHAREHOLDERS_SUCCEEDED } from 'features/offer/edit/client/shareholders/actionTypes';
import { LOAD_PROPERTIES_SUCCEEDED, LOAD_PROPERTY_DETAILS_SUCCEEDED } from 'features/offer/edit/client/properties/actionTypes';
import { SHAREHOLDERS_UPDATED } from 'features/offer/edit/client/shareholders/eventTypes';
import { DOCUMENT_PARTNER_PROPERTIES_UPDATED } from 'features/offer/edit/client/properties/eventTypes';
import { LOAD_DOCUMENT_VERIFICATIONS_SUCCEEDED } from 'features/document/edit/verification/actionTypes';
import { PARTNER_BANK_ACCOUNTS_UPDATED } from 'features/offer/edit/client/bankAccount/eventTypes';
import { ACTIVITY_FIELDS_INVALIDATED } from 'features/offer/edit/client/activities/eventTypes';
import {
  PARTNER_QUESTIONNAIRE_FINISH_COMPLETED,
  PARTNER_QUESTIONACTIONS_INVALIDATED,
  PARTNER_QUESTIONS_INVALIDATED
} from 'features/offer/edit/client/questionnaire/eventTypes';
import { LOAD_OFFER_QUESTIONS_SUCCEEDED } from 'features/offer/edit/client/questionnaire/actionTypes';
import { LOAD_OFFER_QUESTIONACTIONS_SUCCEEDED } from 'features/offer/edit/client/questionnaire/actionTypes';
import { LOAD_RESPONSIBLE_CHANGE_OFFERS_SUCCEEDED } from 'features/offer/edit/responsibleChange/actionTypes';
import { LOAD_OFFER_PARTNER_ELIGIBILITY_SUCCESS } from 'features/offer/edit/client/eligibility/actionTypes';
import { LOAD_OFFER_PARTNER_KNOCKOUT_CRITERIA_SUCCESS } from 'features/offer/edit/client/knockoutCriteria/actionTypes';
import { OFFER_PARTNER_KNOCKOUT_CRITERIA_INVALIDATED } from 'features/offer/edit/client/knockoutCriteria/eventTypes';
import { OFFER_RESPONSIBLE_INVALIDATED } from 'features/offer/edit/responsibleChange/eventTypes';
import { LOAD_RESPONSIBLE_FOLDERS_SUCCEEDED } from '../../folder/list/actionTypes';
import { FOLDER_RESPONSIBLE_INVALIDATED } from '../../folder/edit/allocateResponsible/eventTypes';
import {
  LOAD_GENERICFILTERPARAMS_SUCCEEDED,
  LOAD_GENERICFILTEROPERATORS_SUCCEEDED,
  LOAD_GENERICFILTERPARAMVALUES_SUCCEEDED
} from 'features/genericFilters/actionTypes';
import { CONTRACT_LIST_RETRIEVED } from 'features/offer/edit/contract/eventTypes';
import { OFFER_LEASING_EXPENSES_INVALIDATED } from 'features/offer/edit/financials/leasingExpenses/eventTypes';
import { COMMISSIONS_INVALIDATED } from 'features/offer/edit/financials/commissions/eventTypes';
import {
  OFFER_PARTNER_VERIFICATIONS_INVALIDATED,
  DOCUMENT_VERIFICATION_UPDATED,
  DOCUMENT_VERIFICATION_STARTED
} from 'features/offer/edit/client/verification/eventTypes';
import { LOAD_OFFER_DECISION_FINANCED_ASSETS_CHART_SUCCESS } from 'features/offer/edit/decision/financedChart/actionTypes';
import { OFFER_DECISION_FINNANCING_ASSETS_CHART_INVALIDATED } from 'features/offer/edit/decision/financedChart/eventTypes';
import { LOAD_CONTACT_MODES_SUCCEEDED, UPDATE_CONTACT_MODES_SUCCEEDED } from 'features/offer/edit/client/contactModes/actionTypes';

export default function entitiesReducer(state = initialState.entities, action) {
  switch (action.type) {
    case LOAD_OFFER_ASSET_CLIENT_SUCCEEDED: {
      return merge({}, state, normalize(action.payload.client, loadOfferPartnerSchema).entities);
    }

    case LOAD_OFFER_PARTNER_SUCCEEDED: {
      const result = merge({}, state, normalize(action.payload.partner, loadOfferPartnerSchema).entities);
      if (result.offerPartners[action.payload.partnerId]) {
        result.offerPartners[action.payload.partnerId].addresses =
          result.offerPartners[action.payload.partnerId].addresses?.filter(x => x.partnerAddressId) ?? [];
      }
      return result;
    }

    case LOAD_OFFER_ASSOCIATED_PARTNERS_SUCCEEDED: {
      return {
        ...state,
        associatedPartners: {
          ...state.associatedPartners,
          [action.key]: action.payload
        }
      };
    }

    case OFFER_ASSET_CLIENT_DATA_INVALIDATED: {
      return {
        ...state,
        offerPartners: {
          [action.payload.partnerId]: {
            masterPartnerId: null,
            partnerGroupId: null,
            partnerId: action.payload.partnerId,
            contactData: {},
            contactMode: {},
            dealerContactInfos: [],
            dealerContactEmails: [],
            dealerContactPhones: [],
            generalData: { partnerName: '' },
            partnerType: action.payload.partnerType,
            agreesWithPersonalDataUse: false,
            agreesWithPersonalDataUseReadOnly: false,
            addresses: []
          }
        }
      };
    }

    case SET_DEFAULT_ADDRESS_ON_OFFER_CLIENT: {
      let addresses = state.offerPartners[action.partnerId]?.addresses?.map(addr => Object.assign({}, addr));
      let defaultAddress = [];

      if (addresses && Array.isArray(addresses)) addresses.unshift(action.defaultAddress);
      else defaultAddress.push(action.defaultAddress);

      return {
        ...state,
        offerPartners: {
          ...state.offerPartners,
          [action.partnerId]: {
            ...state.offerPartners[action.partnerId],
            addresses: addresses ?? defaultAddress
          }
        }
      };
    }

    case OFFER_PARTNER_UPDATED: {
      const offerPartners = { ...state.offerPartners };
      delete offerPartners[action.event.eventBody.partnerId];
      delete offerPartners[defaultPartnerId];
      return {
        ...state,
        offerPartners
      };
    }

    case OFFER_ASSET_CLIENT_UPDATED: {
      const offerPartners = { ...state.offerPartners };
      delete offerPartners[action.event.eventBody.clientId];
      return {
        ...state,
        offerPartners
      };
    }

    case LOAD_FOLDER_PARTNER_SUCCESS: {
      return merge({}, state, normalize(action.payload.partner, partnerSchema).entities);
    }

    case LOAD_DECISION_SUMMARY_SUCCESS: {
      const decision = { summary: { ...action.payload }, id: action.id };
      return merge({}, state, normalize(decision, decisionSchema).entities);
    }

    case LOAD_APPROVAL_LEVELS_SUCCESS: {
      const approvalLevels = { approvalLevels: action.payload, id: action.id };
      const normalizedPayload = normalize(approvalLevels, decisionSchema).entities;

      const newDecision = {
        ...state.decision,
        [action.id]: {
          ...state.decision[action.id],
          approvalLevels: normalizedPayload?.decision?.[action.id]?.approvalLevels
        }
      };

      const newApprovalLevels = { ...state.approvalLevels };

      if (normalizedPayload?.approvalLevels)
        for (const key of Object.keys(normalizedPayload.approvalLevels)) newApprovalLevels[key] = normalizedPayload.approvalLevels[key];

      return {
        ...state,
        decision: newDecision,
        approvalLevels: newApprovalLevels
      };
    }

    case LOAD_FOLDER_OFFERS_SUCCEEDED: {
      const folderOffers = { folderOffers: action.payload, id: action.id };
      return merge({}, state, normalize(folderOffers, decisionSchema).entities);
    }

    case APPROVAL_LEVEL_UPDATED: {
      const mergedApprovalLevels = state.decision[action.id].approvalLevels.map(x => {
        if (action.payload.approvalLevelId === x) return action.payload;
        else return state.approvalLevels[x];
      });
      const approvalLevels = { approvalLevels: mergedApprovalLevels, id: action.id };
      return merge({}, state, normalize(approvalLevels, decisionSchema).entities);
    }

    case LOAD_APPROVALS_HISTORY_SUCCESS: {
      const approvalsHistory = { approvalsHistory: action.payload, id: action.id };
      return merge({}, state, normalize(approvalsHistory, decisionSchema).entities);
    }

    case LOAD_CONTRACT_GEN_PERMISSIONS_SUCCESS: {
      const permissions = { contractGenPermissions: { ...action.payload }, id: action.id };
      return merge({}, state, normalize(permissions, decisionSchema).entities);
    }

    case LOAD_EXPOSURE_SUCCESS: {
      const exposure = { ...action.payload, id: action.id };
      return merge({}, state, normalize(exposure, exposureSchema).entities);
    }

    case EXPOSURE_CURRENCIES_RETRIEVED: {
      const { documentId, siteId } = action.event.eventBody;
      const key = `${documentId}-${siteId}`;
      const currencies = { exposureCurrencies: action.event.eventBody.exposureCurrencies, id: key };
      return merge({}, state, normalize(currencies, exposureSchema).entities);
    }

    case LOAD_DOCUMENT_VERIFICATIONS_SUCCEEDED: {
      const result = action.payload;

      return {
        ...state,
        verifications: {
          ...state.verifications,
          [action.id]: result
        }
      };
    }

    case LOAD_STATE_TRANSITIONS_SUCCESS: {
      return {
        ...state,
        stateTransitions: {
          ...state.stateTransitions,
          [action.id]: { items: action.payload }
        }
      };
    }

    case SAVE_OFFER_ASSET_CLIENT_SUCCEEDED: {
      const partner = { ...action.payload.partner, partnerId: action.payload.partnerId };
      return merge({}, state, normalize(partner, loadOfferPartnerSchema).entities);
    }

    case OFFER_PARTNER_DELETED: {
      const key = action.event.eventBody.partnerId;
      const offerPartners = { ...state.offerPartners };
      delete offerPartners[key];
      return {
        ...state,
        offerPartners
      };
    }

    case OFFER_PARTNER_RESET: {
      const key = action.event.eventBody.partnerId;
      const offerPartners = { ...state.offerPartners };
      const partner = offerPartners[key];
      return {
        ...state,
        deleting: false,
        offerPartners: {
          ...state.offerPartners,
          [key]: {
            ...partner,
            generalData: {
              partnerName: ''
            },
            contactMode: {},
            addresses: [],
            masterPartnerId: null
          }
        }
      };
    }

    case DOCUMENT_SCORING_INVALIDATED: {
      const key = `${action.event.eventBody.documentId}-${action.event.eventBody.siteId}`;
      const scoring = { ...state.scoring };
      delete scoring[key];
      return {
        ...state,
        scoring
      };
    }

    case LOAD_DOCUMENT_SCORING_RESULT_SUCCESS: {
      return {
        ...state,
        scoring: {
          ...state.scoring,
          [action.id]: {
            ...state.scoring[action.id],
            scoringResult: action.payload
          }
        }
      };
    }

    case LOAD_DOCUMENT_SCORING_GRIDS_SUCCESS: {
      return {
        ...state,
        scoring: {
          ...state.scoring,
          [action.id]: {
            ...state.scoring[action.id],
            scoringGrids: action.payload
          }
        }
      };
    }

    case OFFER_PARTNER_KNOCKOUT_CRITERIA_UPDATED: {
      const { partnerId: key, knockoutCriteria } = action.event.eventBody;
      const existingList = state.offerPartnerKnockoutCriteria[key]?.knockoutCriteria?.knockoutCriteria;

      if (!existingList) return { ...state };
      const newList = JSON.parse(JSON.stringify(existingList));

      knockoutCriteria.forEach(kc => {
        const index = existingList.findIndex(a => a.scoringKnockoutCriterionId === kc.scoringKnockoutCriterionId);
        if (index !== -1) newList[index] = { ...kc, orderNo: newList[index]?.orderNo };
      });

      return {
        ...state,
        offerPartnerKnockoutCriteria: {
          ...state.offerPartnerKnockoutCriteria,
          [key]: {
            ...state.offerPartnerKnockoutCriteria[key],
            knockoutCriteria: {
              ...state.offerPartnerKnockoutCriteria[key].knockoutCriteria,
              knockoutCriteria: newList
            }
          }
        }
      };
    }

    case DOCUMENT_SCORING_KNOCKOUT_CRITERIA_UPDATED: {
      const { documentId, siteId, documentScoringGridId, knockoutCriteria } = action.event.eventBody;
      const key = `${documentId}-${siteId}`;
      const existing = state.scoring[key];

      if (!existing) {
        return {
          ...state
        };
      }

      const index = existing.scoringGrids.findIndex(a => a.documentScoringGridId === documentScoringGridId);
      const existingKnockoutCriterias = existing.scoringGrids[index]?.knockoutCriteria;
      if (!existingKnockoutCriterias) return { ...state };
      const newList = JSON.parse(JSON.stringify(existingKnockoutCriterias));

      knockoutCriteria.forEach(kc => {
        const index = existingKnockoutCriterias.findIndex(a => a.scoringKnockoutCriterionId === kc.scoringKnockoutCriterionId);
        if (index !== -1) newList[index] = { ...kc, orderNo: newList[index]?.orderNo };
      });

      if (index !== -1) {
        return {
          ...state,
          scoring: {
            ...state.scoring,
            [key]: {
              ...state.scoring[key],
              scoringGrids: Object.assign([...existing.scoringGrids], {
                [index]: {
                  ...existing.scoringGrids[index],
                  knockoutCriteria: newList
                }
              })
            }
          }
        };
      } else {
        return {
          ...state
        };
      }
    }

    case DOCUMENT_SCORING_CATEGORY_UPDATED: {
      const { documentId, siteId, documentScoringGridId, scoringClassificationId, scoringCategories } = action.event.eventBody;
      const key = `${documentId}-${siteId}`;
      const existing = state.scoring[key];

      if (!existing) {
        return {
          ...state
        };
      }
      const index = existing.scoringGrids.findIndex(a => a.documentScoringGridId === documentScoringGridId);

      if (index !== -1) {
        const classificationIndex = existing.scoringGrids[index].classifications.findIndex(
          a => a.scoringClassificationId === scoringClassificationId
        );

        return {
          ...state,
          scoring: {
            ...state.scoring,
            [key]: {
              ...existing,
              scoringGrids: Object.assign([...existing.scoringGrids], {
                [index]: {
                  ...existing.scoringGrids[index],
                  classifications: Object.assign([...existing.scoringGrids[index].classifications], {
                    [classificationIndex]: {
                      ...existing.scoringGrids[index].classifications[classificationIndex],
                      categories: scoringCategories
                    }
                  })
                }
              })
            }
          }
        };
      } else {
        return {
          ...state
        };
      }
    }

    case DOCUMENT_SCORING_FINANCIAL_INDICATORS_UPDATED: {
      const { documentId, siteId, documentScoringGridId, scoringClassificationId, financialIndicators, periodDimensions } =
        action.event.eventBody;
      const key = `${documentId}-${siteId}`;
      const existing = state.scoring[key];

      if (!existing) {
        return {
          ...state
        };
      }
      const index = existing.scoringGrids.findIndex(a => a.documentScoringGridId === documentScoringGridId);

      if (index !== -1) {
        const classificationIndex = existing.scoringGrids[index].classifications.findIndex(
          a => a.scoringClassificationId === scoringClassificationId
        );
        return {
          ...state,
          scoring: {
            ...state.scoring,
            [key]: {
              ...existing,
              scoringGrids: Object.assign([...existing.scoringGrids], {
                [index]: {
                  ...existing.scoringGrids[index],
                  classifications: Object.assign([...existing.scoringGrids[index].classifications], {
                    [classificationIndex]: {
                      ...existing.scoringGrids[index].classifications[classificationIndex],
                      financialIndicators: financialIndicators
                    }
                  }),
                  financialIndicatorPeriodDimensions: periodDimensions
                }
              })
            }
          }
        };
      } else {
        return {
          ...state
        };
      }
    }

    case DOCUMENT_FINANCIAL_INDICATOR_DIMENSIONS_UPDATED: {
      const { documentId, siteId, documentScoringGridId, periodDimensions } = action.event.eventBody;
      const key = `${documentId}-${siteId}`;
      const existing = state.scoring[key];

      if (!existing) {
        return {
          ...state
        };
      }
      const index = existing.scoringGrids.findIndex(a => a.documentScoringGridId === documentScoringGridId);

      if (index !== -1) {
        return {
          ...state,
          scoring: {
            ...state.scoring,
            [key]: {
              ...existing,
              scoringGrids: Object.assign([...existing.scoringGrids], {
                [index]: {
                  ...existing.scoringGrids[index],
                  financialIndicatorPeriodDimensions: periodDimensions
                }
              })
            }
          }
        };
      } else {
        return {
          ...state
        };
      }
    }

    case SAVE_OFFER_PARTNER_SUCCEEDED: {
      const partner = { ...action.payload.partner, partnerId: action.payload.partnerId };
      return merge({}, state, normalize(partner, loadOfferPartnerSchema).entities);
    }

    case OFFER_ASSOCIATED_PARTNER_DELETED: {
      const { partnerId, documentId, siteId } = action.event.eventBody;
      const offerPartners = { ...state.offerPartners };
      delete offerPartners[action.event.eventBody.partnerId];
      const key = `${documentId}-${siteId}`;

      return {
        ...state,
        offerPartners,
        associatedPartners: state.associatedPartners[key].filter(p => p.partnerId !== partnerId)
      };
    }

    case LOAD_WARRANTIES_DATA_SUCCESS: {
      return {
        ...state,
        warranties: {
          ...state.warranties,
          [action.id]: action.payload
        }
      };
    }

    case LOAD_WARRANTIES_SUMMARY_SUCCESS: {
      return {
        ...state,
        warrantiesSummary: {
          ...state.warrantiesSummary,
          [action.id]: action.payload
        }
      };
    }

    case WARRANTIES_INVALIDATED: {
      const key = `${action.event.eventBody.documentId}-${action.event.eventBody.siteId}`;
      const warrantyData = { ...state.warranties };
      delete warrantyData[key];
      const warrantyDataSummary = { ...state.warrantiesSummary };
      delete warrantyDataSummary[key];
      return {
        ...state,
        warranties: warrantyData,
        warrantiesSummary: warrantyDataSummary
      };
    }

    case OFFER_PARTNER_ALL_INCOME_TYPES_INVALIDATED: {
      const { partnerId } = action.event.eventBody;
      const offerPartnerIncomeTypes = { ...state.offerPartnerIncomeTypes };
      delete offerPartnerIncomeTypes[partnerId];
      return {
        ...state,
        offerPartnerIncomeTypes: offerPartnerIncomeTypes
      };
    }

    case ALL_WARRANTIES_DELETED: {
      const warranties = { ...state.warranties };
      delete warranties[action.id];
      return {
        ...state,
        warranties
      };
    }

    case WARRANTY_DELETED: {
      const existingWarranties = state.warranties[action.id];

      if (!existingWarranties) {
        return {
          ...state
        };
      }

      const index = existingWarranties.findIndex(a => a.warrantyId === action.warrantyId);

      if (index === -1) {
        return {
          ...state
        };
      }

      const newWarranties = [...existingWarranties];
      delete newWarranties[index];

      const existingSummary = state.warrantiesSummary[action.id];
      const newWarrantiesSummary = [...existingSummary.filter(z => z.warrantyId !== action.warrantyId)];

      return {
        ...state,
        warranties: {
          ...state.warranties,
          [action.id]: newWarranties.filter(x => x !== undefined)
        },
        warrantiesSummary: {
          ...state.warrantiesSummary,
          [action.id]: newWarrantiesSummary
        }
      };
    }

    case LOAD_PARTNER_CONSENTS_SUCCEEDED:
      return merge({}, state, normalize(action.payload.partnerConsents, loadOfferPartnerConsentsSchema).entities);

    case LOAD_PARTNER_BANK_ACCOUNTS_SUCCEEDED:
      var result = { bankAccounts: action.payload, id: action.id };
      if (!action.payload) result = { bankAccounts: null, id: action.id };
      return merge({}, state, normalize(result, loadOfferPartnerBankAccountsSchema).entities);

    case LOAD_OFFER_QUESTIONS_SUCCEEDED:
      return merge({}, state, normalize(action.payload, loadOfferPartnerQuestionsSchema).entities);

    case LOAD_OFFER_QUESTIONACTIONS_SUCCEEDED:
      return merge({}, state, normalize(action.payload, loadOfferPartnerQuestionActionsSchema).entities);

    case PARTNER_QUESTIONNAIRE_FINISH_COMPLETED: {
      const { documentId, siteId, partnerId, questions, questionActions } = action.event.eventBody;
      let key = `${documentId}-${siteId}-${partnerId}`;
      let newOfferPartnerQuestions = state.offerPartnerQuestions;
      newOfferPartnerQuestions[key] = {
        ...state.offerPartnerQuestions[key],
        questions: questions
      }
      let newOfferPartnerQuestionActions = state.offerPartnerQuestionActions;
      newOfferPartnerQuestionActions[key] = {
        ...state.offerPartnerQuestionActions[key],
        questionActions: questionActions
      }
      return {
        ...state,
        offerPartnerQuestions: newOfferPartnerQuestions,
        offerPartnerQuestionActions: newOfferPartnerQuestionActions
      };
    }

    case PARTNER_QUESTIONACTIONS_INVALIDATED: {
      const { documentId, siteId, partnerId } = action.event.eventBody;
      let key = `${documentId}-${siteId}-${partnerId}`;
      let newOfferPartnerQuestionActions = state.offerPartnerQuestionActions;
      newOfferPartnerQuestionActions[key] = {
        ...state.offerPartnerQuestionActions[key],
        questionActions: []
      }
      return {
        ...state,
        offerPartnerQuestionActions: newOfferPartnerQuestionActions
      };
    }
    case PARTNER_QUESTIONS_INVALIDATED: {
      const { documentId, siteId, partnerId } = action.event.eventBody;
      let key = `${documentId}-${siteId}-${partnerId}`;
      let newOfferPartnerQuestions = state.offerPartnerQuestions;
      newOfferPartnerQuestions[key] = {
        ...state.offerPartnerQuestions[key],
        questions: []
      }
      return {
        ...state,
        offerPartnerQuestions: newOfferPartnerQuestions
      };
    }

    case LOAD_PARTNER_MONTHLY_OBLIGATIONS_SUCCEEDED:
      return merge({}, state, normalize(action.payload.partnerMonthlyObligations, loadOfferPartnerMonthlyObligationsSchema).entities);

    case LOAD_OFFER_PARTNER_ELIGIBILITY_SUCCESS:
      return merge({}, state, normalize(action.payload, loadOfferPartnerEligibilitiesSchema).entities);

    case LOAD_OFFER_PARTNER_KNOCKOUT_CRITERIA_SUCCESS:
      return merge({}, state, normalize(action.payload, loadOfferPartnerKnockoutCriteriaSchema).entities);

    case OFFER_PARTNER_KNOCKOUT_CRITERIA_INVALIDATED: {
      const { partnerId } = action.event.eventBody;
      return {
        ...state,
        offerPartnerKnockoutCriteria: {
          ...state.offerPartnerKnockoutCriteria,
          [partnerId]: {}
        }
      };
    }

    case LOAD_ACTIVITY_FIELDS_SUCCEEDED:
      return merge({}, state, normalize(action.payload.partnerActivityFields, loadOfferPartnerActivitiesSchema).entities);
    case LOAD_PARTNER_VERIFICATIONS_SUCCEEDED:
      return merge({}, state, normalize(action.payload.partnerVerifications, loadOfferPartnerVerificationsSchema).entities);

    case OFFER_PARTNER_VERIFICATIONS_INVALIDATED: {
      const { partnerId } = action.event.eventBody;
      return {
        ...state,
        offerPartnerVerifications: {
          ...state.offerPartnerVerifications,
          [partnerId]: {}
        }
      };
    }

    case DOCUMENT_VERIFICATION_UPDATED: {
      const { verification } = action.event.eventBody;
      let key = `${verification.documentId}-${verification.siteId}`;
      let desiredKey;
      let desiredIndex;
      const verificationsIndex = state.verifications[key].findIndex(item => item.verificationId == verification.verificationId);
      let partnerVerifications = {
        ...state.offerPartnerVerifications
      };
      Object.keys(partnerVerifications).some(key => {
        const verifications = partnerVerifications[key].verifications;
        return verifications.some((v, index) => {
          if (v.entityId === verification.entityId && v.verificationId === verification.verificationId) {
            desiredKey = key;
            desiredIndex = index;
            return true;
          }
        });
      });

      return {
        ...state,
        offerPartnerVerifications: {
          ...state.offerPartnerVerifications,
          [desiredKey]: {
            ...state.offerPartnerVerifications[desiredKey],
            verifications: Object.assign([...state.offerPartnerVerifications[desiredKey].verifications], {
              [desiredIndex]: {
                ...verification
              }
            })
          }
        },
        verifications: {
          ...state.verifications,
          [key]: Object.assign([...state.verifications[key]], {
            [verificationsIndex]: {
              ...verification
            }
          })
        }
      };
    }

    case DOCUMENT_VERIFICATION_STARTED: {
      const { verification } = action.event.eventBody;
      const key = `${verification.documentId}-${verification.siteId}`;
      const verifications = state.verifications[key] ? [...state.verifications[key]] : [];
      const offerPartnerVerifications = state.offerPartnerVerifications[action.event.eventBody.partnerId]?.verifications
        ? state.offerPartnerVerifications[action.event.eventBody.partnerId]?.verifications
        : [];

      const partnerVerifications = {
        ...state.offerPartnerVerifications[action.event.eventBody.partnerId],
        verifications: [...offerPartnerVerifications, verification]
      };

      return {
        ...state,
        offerPartnerVerifications: {
          ...state.offerPartnerVerifications,
          [action.event.eventBody.partnerId]: partnerVerifications
        },
        verifications: {
          ...state.verifications,
          [key]: [...verifications, verification]
        }
      };
    }

    case LOAD_OFFER_PARTNER_INCOME_TYPES_SUCCEEDED:
      return merge({}, state, normalize(action.payload.partnerIncomeTypes, loadOfferPartnerIncomeTypesSchema).entities);

    case LOAD_OFFER_PARTNER_INCOME_DATA_SUCCEEDED:
      return merge({}, state, normalize(action.payload.partnerIncomeData, loadOfferPartnerIncomeDataSchema).entities);

    case PARTNER_BANK_ACCOUNTS_UPDATED: {
      const { partnerId, bankAccounts } = action.event.eventBody;
      return {
        ...state,
        offerPartnerBankAccounts: {
          ...state.offerPartnerBankAccounts,
          [partnerId]: {
            ...state.offerPartnerBankAccounts[partnerId],
            bankAccounts: bankAccounts
          }
        }
      };
    }

    case SHAREHOLDERS_UPDATED: {
      const { partnerId, shareholders } = action.event.eventBody;
      return {
        ...state,
        offerPartnerShareholders: {
          ...state.offerPartnerShareholders,
          [partnerId]: shareholders
        }
      };
    }

    case DOCUMENT_PARTNER_PROPERTIES_UPDATED: {
      const { partnerId, properties } = action.event.eventBody;
      return {
        ...state,
        offerPartnerProperties: {
          ...state.offerPartnerProperties,
          [partnerId]: properties
        }
      };
    }

    case OFFER_PARTNER_CONSENTS_UPDATED: {
      const { partnerId, options } = action.event.eventBody;
      const existing = state.offerPartnerConsents[partnerId];
      if (existing) {
        const consents = existing.consents.slice();
        consents.forEach((consent, index) => {
          const updated = options.find(a => a.consentTypeId === consent.consentTypeId);
          if (updated) consents[index] = updated;
        });

        return {
          ...state,
          offerPartnerConsents: {
            ...state.offerPartnerConsents,
            [partnerId]: {
              ...existing,
              ...consents
            }
          }
        };
      }
      return { ...state };
    }

    case OFFER_PARTNER_MONTHLY_OBLIGATIONS_UPDATED: {
      const { partnerId, values } = action.event.eventBody;
      const existing = state.offerPartnerMonthlyObligations[partnerId];
      if (existing) {
        const monthlyObligations = existing.monthlyObligations.slice();
        monthlyObligations.forEach((obligation, index) => {
          const updated = values.find(a => a.partnerIncomeSourceDetailId === obligation.partnerIncomeSourceDetailId);
          if (updated) monthlyObligations[index] = updated;
        });

        return {
          ...state,
          offerPartnerMonthlyObligations: {
            ...state.offerPartnerMonthlyObligations,
            [partnerId]: {
              ...existing,
              ...monthlyObligations
            }
          }
        };
      }
      return { ...state };
    }

    case OFFER_PARTNER_INCOME_TYPE_UPDATED: {
      const { partnerId, incomeTypeId, selected } = action.event.eventBody;
      const existing = state.offerPartnerIncomeTypes[partnerId];

      if (!existing) {
        return {
          ...state
        };
      }
      const index = existing.incomes.findIndex(a => a.incomeTypeId === incomeTypeId);

      if (index !== -1) {
        const clientIncomeType = { ...existing.incomes[index], selected };

        return {
          ...state,
          offerPartnerIncomeTypes: {
            ...state.offerPartnerIncomeTypes,
            [partnerId]: {
              ...existing,
              incomes: collection.update(index, clientIncomeType, existing.incomes)
            }
          }
        };
      } else {
        return {
          ...state
        };
      }
    }

    case LOAD_DOCUMENT_SCORING_HISTORY_SESSION_GRIDS_SUCCESS: {
      const { key, sessionID, scoringHistoryGridSession } = action.payload;

      const scoringHistory = state.scoringHistory.scoringSessions[key];
      if (!scoringHistory) return;

      const scoringHistorySession = scoringHistory[sessionID];
      if (!scoringHistorySession) return;

      return {
        ...state,
        scoringHistory: {
          ...state.scoringHistory,
          scoringSessions: {
            ...state.scoringHistory.scoringSessions,
            [key]: {
              ...state.scoringHistory.scoringSessions[key],
              [sessionID]: state.scoringHistory.scoringSessions[key][sessionID]
            }
          },
          scoringSessionsGrids: {
            ...state.scoringHistory.scoringSessionsGrids,
            [sessionID]: scoringHistoryGridSession
          }
        }
      };
    }

    case LOAD_DOCUMENT_SCORING_HISTORY_SESSION_SUCCESS: {
      if (action.payload.result.length === 0)
        return {
          ...state
        };

      const scoringHistory = action.payload.entities.scoringHistory;

      return {
        ...state,
        scoringHistory: {
          ...state.scoringHistory,
          scoringSessions: {
            ...state.scoringHistory.scoringSessions,
            [action.id]: scoringHistory
          }
        }
      };
    }

    case LOAD_FINANCED_OFFERS_SUCCEEDED: {
      return {
        ...state,
        financedOffers: {
          ...state.financedOffers,
          ...action.entities.financedOffers
        }
      };
    }

    case LOAD_FOLDER_OFFER_PARTNERS_SUCCEEDED: {
      return {
        ...state,
        offerPartners: {
          ...state.offerPartners,
          ...action.entities.offerPartners
        }
      };
    }

    case LOAD_FINANCED_OFFERS_FOR_CONTRACT_GENERATION_SUCCEEDED: {
      return {
        ...state,
        contractGeneration: {
          ...state.contractGeneration,
          ...action.entities.contractGeneration
        }
      };
    }

    case LOAD_CLIENT_OFFERS_SUCCEEDED: {
      return {
        ...state,
        clientOffers: {
          ...state.clientOffers,
          ...action.entities.clientOffers
        }
      };
    }

    case FOLDER_FINANCED_OFFERS_UPDATED: {
      return {
        ...state,
        financedOffers: state.financedOffers,
        clientOffers: state.clientOffers
      };
    }

    case BLOCK_FACILITY_SUBLIMIT_AMOUNT_MANUALLY_STARTED: {
      return {
        ...state,
        financedOffers: {
          ...state.financedOffers,
          [action.payload.documentId]: {
            ...state.financedOffers[action.payload.documentId],
            blockFacilitySublimitAmountExecuting: true
          }
        },
      };
    }

    case BLOCK_FACILITY_SUBLIMIT_AMOUNT_MANUALLY_SUCCEEDED: {
      return {
        ...state,
        financedOffers: {
          ...state.financedOffers,
          [action.payload.documentId]: {
            ...state.financedOffers[action.payload.documentId],
            blockFacilitySublimitAmountExecuting: false,
            isFacilityBlocked: true
          }
        },
      };
    }

    case BLOCK_FACILITY_SUBLIMIT_AMOUNT_MANUALLY_FAILED: {
      return {
        ...state,
        financedOffers: {
          ...state.financedOffers,
          [action.payload.documentId]: {
            ...state.financedOffers[action.payload.documentId],
            blockFacilitySublimitAmountExecuting: false
          }
        },
      };
    }

    case LOAD_CHECKLIST_SUCCESS: {
      const checklist = { checklistDetails: action.payload, id: action.id };
      return merge({}, state, normalize(checklist, checkListSchema).entities);
    }

    case CHECKLIST_INVALIDATED: {
      const key = `${action.event.eventBody.documentId}-${action.event.eventBody.siteId}`;
      const checklist = { ...state.checklist };
      const checklistDetails = { ...state.checklistDetails };

      checklist[key].checklistDetails.forEach(item => {
        delete checklistDetails[item];
      });

      delete checklist[key];

      return {
        ...state,
        checklist: checklist,
        checklistDetails: checklistDetails
      };
    }

    case LOAD_DOCUMENT_PROPERTIES_SUCCESS: {
      return {
        ...state,
        documentProperties: {
          ...state.documentProperties,
          [action.id]: action.payload.documentProperties
        }
      };
    }

    case LOAD_PROPERTY_DETAILS_SUCCESS: {
      return {
        ...state,
        propertyDetails: {
          ...state.propertyDetails,
          [action.id]: action.payload.propertyDetails
        }
      };
    }

    case UPDATE_DOCUMENT_PROPERTIES_SUCCEEDED: {
      return {
        ...state,
        documentProperties: {
          ...state.documentProperties,
          [action.id]: action.payload.documentProperties
        }
      };
    }

    case LOAD_COMMISSIONS_SUCCESS: {
      const commissions = { commissionsDetails: action.payload, id: action.id };
      return merge({}, state, normalize(commissions, commissionsSchema).entities);
    }

    case LOAD_CALCULATION_METHODS_SUCCESS: {
      const calculationMethods = action.payload;
      return {
        ...state,
        calculationMethods: {
          ...state.calculationMethods,
          [action.id]: {
            ...calculationMethods
          }
        }
      };
    }

    case LEASING_EXPENSES_UPDATED: {
      return {
        ...state,
        offerLeasingExpenses: {}
      };
    }

    case LOAD_LEASING_EXPENSES_SUCCESS: {
      const offerLeasingExpenses = { offerLeasingExpensesDetails: action.payload, id: action.id };
      return merge({}, state, normalize(offerLeasingExpenses, leasingExpensesSchema).entities);
    }

    case OFFER_LEASING_EXPENSES_INVALIDATED: {
      const key = `${action.event.eventBody.documentId}-${action.event.eventBody.siteId}`;
      const expenses = { ...state.offerLeasingExpenses };
      const expensesDetails = { ...state.offerLeasingExpensesDetails };
      const offerExpenses = expenses[key];
      offerExpenses?.offerLeasingExpensesDetails?.forEach(item => {
        delete expensesDetails[item];
      });
      delete expenses[key];
      return { ...state, offerLeasingExpenses: { ...expenses }, offerLeasingExpensesDetails: { ...expensesDetails } };
    }

    case COMMISSION_COMPUTED: {
      const { initialCommission, commissionId, calculationMethod, payload } = action;

      return {
        ...state,
        commissionsDetails: {
          ...state.commissionsDetails,
          [initialCommission.documentCommissionId]: {
            ...state.commissionsDetails[initialCommission.documentCommissionId],
            commissionId: commissionId,
            commissionPercent: payload.commissionPercent,
            commissionAmount: payload.commissionAmount,
            calculationMethodId: calculationMethod.calculationMethodId,
            calculationMethodName: calculationMethod.calculationMethodName
          }
        }
      };
    }

    case COMMISSION_ADDED: {
      const { id, commission } = action;

      return {
        ...state,
        commissions: {
          ...state.commissions,
          [id]: {
            ...state.commissions[id],
            commissionsDetails: [...state.commissions[id].commissionsDetails, commission.documentCommissionId]
          }
        },
        commissionsDetails: {
          ...state.commissionsDetails,
          [commission.documentCommissionId]: {
            ...(state.commissionsDetails ? state.commissionsDetails[commission.documentCommissionId] : {}),
            ...commission
          }
        }
      };
    }

    case COMMISSION_DELETED: {
      const { id, commission } = action;
      const initialCommissions = { ...state.commissions };
      const commissionsDetails = { ...state.commissionsDetails };
      const modifiedCommissionsDetail = initialCommissions[id].commissionsDetails.filter(
        item => item !== commission.document.documentCommissionId
      );
      delete commissionsDetails[commission.documentCommissionId];
      return {
        ...state,
        commissions: {
          ...state.commissions,
          [id]: {
            ...state.commissions[id],
            commissionsDetails: modifiedCommissionsDetail
          }
        },
        commissionsDetails
      };
    }

    case COMMISSIONS_UPDATED: {
      const commissions = { commissionsDetails: action.payload, id: action.id };
      const commissionsIds = commissions.commissionsDetails.map(item => {
        return item.documentCommissionId;
      });
      let newDetails = {};
      commissions.commissionsDetails.forEach(x => {
        newDetails[x.documentCommissionId] = x;
      });

      return {
        ...state,
        commissions: {
          ...state.commissions,
          [commissions.id]: {
            ...state.commissions[commissions.id],
            commissionsDetails: commissionsIds
          }
        },
        commissionsDetails: { ...newDetails }
      };
    }

    case COMMISSIONS_INVALIDATED: {
      return {
        ...state,
        commissions: emptyObject
      };
    }

    case DOCUMENT_LIST_EXPORTED: {
      const key = `${action.event.eventBody.documentId}-${action.event.eventBody.siteId}`;
      let docs = [...state.exportDocuments[key]];
      action.event.eventBody.list.forEach(item => {
        const index = docs.findIndex(e => e.documentPrintId === item.documentPrintId);
        if (index > -1) {
          docs[index] = Object.assign({}, docs[index], { ...item, isUploaded: true, selected: false });
        }
      });
      const exportDocuments = { exportDocuments: Object.assign([], docs, { isLoaded: true }), id: key };
      return merge({}, state, normalize(exportDocuments, documentsSchema).entities);
    }

    case LOAD_DOCUMENT_SIGNEES_SUCCEEDED: {
      return {
        ...state,
        documentSignees: {
          ...state.documentSignees,
          [action.id]: action.payload
        }
      };
    }

    case DOCUMENT_SIGNEES_UPDATED: {
      return {
        ...state,
        documentSignees: {
          ...state.documentSignees,
          [action.id]: action.payload
        }
      };
    }

    case DOCUMENT_SIGNEES_INVALIDATED: {
      const { documentId, siteId } = action.event.eventBody;
      const key = `${documentId}-${siteId}`;
      return {
        ...state,
        documentSignees: { ...state.documentSignees, [key]: [] }
      };
    }

    case LOAD_POSITIONS_SUCCEEDED: {
      const positions = action.payload;
      return merge({}, state, normalize(positions, [positionsSchema]).entities);
    }

    case LOAD_CONTACT_PERSONS_SUCCEEDED: {
      const { contactPersons, key } = action;
      return {
        ...state,
        contactPersons: {
          ...state.contactPersons,
          [key]: contactPersons
        }
      };
    }

    case LOAD_SHAREHOLDERS_SUCCEEDED: {
      const { shareholders, key } = action;
      return {
        ...state,
        offerPartnerShareholders: {
          ...state.offerPartnerShareholders,
          [key]: shareholders
        }
      };
    }

    case LOAD_CONTACT_MODES_SUCCEEDED: {
      let result = state;

      if (action.entities) {
        result = merge({}, state, action.entities);
      }
      return result;
    }

    case UPDATE_CONTACT_MODES_SUCCEEDED: {
      const { contactModes } = action;

      const result = {
        ...state,
        offerPartnerContactModes: { ...state.offerPartnerContactModes }
      };

      contactModes.forEach(x => (result.offerPartnerContactModes[x.partnerContactModeId] = { ...x }));
      return { ...state, offerPartnerContactModes: { ...result.offerPartnerContactModes } };
    }

    case LOAD_CLIENT_ADDRESSES_SUCCEEDED: {
      const { addresses, key } = action;

      const result = normalize({ partnerId: key, addresses }, offerPartnerAddressesSchema).entities;

      return {
        ...state,
        offerPartnerAddresses: {
          ...state.offerPartnerAddresses,
          [key]: { ...result.offerPartnerAddresses[key] }
        }
      };
    }

    case LOAD_GUARANTORS_SUCCEEDED: {
      const { guarantors, key } = action;
      return {
        ...state,
        guarantors: {
          ...state.guarantors,
          [key]: guarantors
        }
      };
    }

    case LOAD_PROPERTIES_SUCCEEDED: {
      const { properties, key } = action;
      return {
        ...state,
        offerPartnerProperties: {
          ...state.offerPartnerProperties,
          [key]: properties
        }
      };
    }

    case LOAD_PROPERTY_DETAILS_SUCCEEDED: {
      const { propertyDetails, propertyId } = action;
      return {
        ...state,
        offerPropertiesDetails: {
          ...state.offerPropertiesDetails,
          [propertyId]: propertyDetails
        }
      };
    }

    case UPDATE_CONTACT_PERSONS_SUCCEEDED: {
      const { contactPersons, partnerId } = action;
      const result = {
        ...state,
        contactPersons: {
          ...state.contactPersons,
          [partnerId]: contactPersons
        }
      };

      return result;
    }

    case UPDATE_ACTIVITY_FIELDS_SUCCEEDED: {
      const { activityFields, partnerId } = action;
      return {
        ...state,
        offerPartnerActivities: {
          ...state.offerPartnerActivities,
          [partnerId]: {
            ...state.offerPartnerActivities[partnerId],
            partnerActivityFields: activityFields
          }
        }
      };
    }

    case ACTIVITY_FIELDS_INVALIDATED: {
      return {
        ...state,
        offerPartnerActivities: {}
      };
    }

    case LOAD_RESPONSIBLE_CHANGE_OFFERS_SUCCEEDED: {
      const responsibleChangeOffers = action.entities.responsibleChangeOffers;

      if (!responsibleChangeOffers) {
        return state;
      }

      return merge({}, state, normalize(responsibleChangeOffers, schemas.responsibleChangeOffers).entities);
    }

    case OFFER_RESPONSIBLE_INVALIDATED: {
      const { documentId, siteId, responsibleId, responsibleName } = action.event.eventBody;
      const key = `${documentId}-${siteId}`;

      if (!state.responsibleChangeOffers[key]) {
        return state;
      }

      return {
        ...state,
        responsibleChangeOffers: {
          ...state.responsibleChangeOffers,
          [key]: {
            ...state.responsibleChangeOffers[key],
            responsibleId: responsibleId,
            responsibleName: responsibleName
          }
        }
      };
    }

    case LOAD_RESPONSIBLE_FOLDERS_SUCCEEDED: {
      const responsibleChangeFolders = action.entities.responsibleChangeFolders;

      if (!responsibleChangeFolders) {
        return state;
      }

      return merge({}, state, normalize(responsibleChangeFolders, schemas.responsibleChangeFolders).entities);
    }

    case FOLDER_RESPONSIBLE_INVALIDATED: {
      const { documentId, siteId, responsibleId, responsibleName } = action.event.eventBody;
      const key = `${documentId}-${siteId}`;

      if (!state.responsibleChangeFolders[key]) {
        return state;
      }

      return {
        ...state,
        responsibleChangeFolders: {
          ...state.responsibleChangeFolders,
          [key]: {
            ...state.responsibleChangeFolders[key],
            responsibleId: responsibleId,
            responsibleName: responsibleName
          }
        }
      };
    }

    case LOAD_GENERICFILTERPARAMS_SUCCEEDED: {
      const genericFilterParams = action.entities.genericFilterParams;

      if (!genericFilterParams) return state;

      return merge({}, state, normalize(genericFilterParams, schemas.genericFilterParams).entities);
    }

    case LOAD_GENERICFILTEROPERATORS_SUCCEEDED: {
      const genericFilterOperators = action.entities.genericFilterOperators;

      if (!genericFilterOperators) return state;

      return merge({}, state, normalize(genericFilterOperators, schemas.genericFilterOperators).entities);
    }

    case LOAD_GENERICFILTERPARAMVALUES_SUCCEEDED: {
      const genericFilterParamValues = action.entities.genericFilterParamValues;

      if (!genericFilterParamValues) return state;

      return merge({}, state, normalize(genericFilterParamValues, schemas.genericFilterParamValues).entities);
    }

    case CONTRACT_LIST_RETRIEVED: {
      const { list } = action.event.eventBody;

      if (!list) {
        return state;
      }

      return merge({}, state, normalize(list, schemas.contractsForRestructuring).entities);
    }

    case LOAD_OFFER_DECISION_FINANCED_ASSETS_CHART_SUCCESS: {
      const key = `${action.payload.documentId}-${action.payload.siteId}`;
      return {
        ...state,
        decision: {
          ...state.decision,
          financedChart: {
            ...state.decision.financedChart,
            [key]: {
              ...action.payload.data
            }
          }
        }
      };
    }

    case OFFER_DECISION_FINNANCING_ASSETS_CHART_INVALIDATED: {
      const key = `${action.event.eventBody.documentId}-${action.event.eventBody.siteId}`;
      return {
        ...state,
        decision: {
          ...state.decision,
          financedChart: {
            ...state.decision.financedChart,
            [key]: {}
          }
        }
      };
    }

    default:
      return state;
  }
}
